// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: assistant connector cards: the built-in GitHub connector plus any number of custom API
// connectors staff add. Everyone connects from the card; staff get a settings button on each card
// and an "Add custom connector" tile that open AssistantConnectorEditor.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import AssistantCapabilities, {capabilityError} from '@app/features/konvo_connections/state/AssistantCapabilities';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {GearIcon, GithubLogoIcon, PlugIcon, PlusIcon} from '@app/features/ui/icons/Icons';
import {IconButton, Notice} from '@app/features/ui/konvo_controls/KonvoControls';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import type {AssistantConnectorView} from '@fluxer/schema/src/domains/konvo/AssistantConnector';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import {blankCustomConnector} from '../utils/AssistantConnectorDraft';
import {openConnectorEditor} from './AssistantConnectorEditor';
import {KonvoConnectorCard} from './KonvoConnectorCard';
import connectorStyles from './KonvoConnectorCard.module.css';

const GITHUB_TOKEN_URL = 'https://github.com/settings/personal-access-tokens/new';

const TokenModal = ({connector}: {connector: AssistantConnectorView}) => {
	const {i18n} = useLingui();
	const [token, setToken] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const d = connector.definition;
	const save = async () => {
		setBusy(true);
		setError('');
		try {
			await AssistantCapabilities.token(d.id, token);
			ModalCommands.pop();
		} catch (e) {
			setError(capabilityError(e, i18n._(msg`The token could not be saved.`)));
		} finally {
			setBusy(false);
		}
	};
	return (
		<Modal.Root size="small" centered onClose={() => ModalCommands.pop()}>
			<Modal.Header title={i18n._(msg`Connect ${d.name}`)} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						{d.shared ? (
							<Trans>
								This token is shared by everyone who can use {d.name}. It is stored encrypted and never shown again.
							</Trans>
						) : (
							<Trans>
								Paste an access token for your own {d.name} account. It is stored encrypted and never shown again.
							</Trans>
						)}
					</Modal.Description>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							if (token.trim()) void save();
						}}
					>
						<Input
							type="password"
							autoComplete="off"
							autoFocus
							label={i18n._(msg`Access token`)}
							placeholder={
								connector.credential_saved ? i18n._(msg`Enter a new token to replace the saved one`) : undefined
							}
							value={token}
							error={error || undefined}
							onChange={(e) => setToken(e.target.value)}
						/>
					</form>
					{d.provider === 'github' && (
						<Modal.Description>
							<Trans>
								Create a fine-grained token with read access to the repositories you want the assistant to see.
							</Trans>{' '}
							<button
								type="button"
								className={connectorStyles.inlineLink}
								onClick={() => void openExternalUrl(GITHUB_TOKEN_URL)}
							>
								<Trans>Create a token on GitHub</Trans>
							</button>
						</Modal.Description>
					)}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()}>
					<Trans>Cancel</Trans>
				</Button>
				<Button disabled={!token.trim()} submitting={busy} onClick={() => void save()}>
					<Trans>Connect</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
};

export const AssistantConnectorCards = observer(({allowAdd = false}: {allowAdd?: boolean}) => {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState('');
	useEffect(() => {
		void AssistantCapabilities.refreshConnectors().catch((e) =>
			setError(capabilityError(e, i18n._(msg`Assistant connections could not be loaded.`))),
		);
		const onFocus = () => void AssistantCapabilities.refreshConnectors().catch(() => {});
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [i18n]);
	const run = async (id: string, fn: () => Promise<unknown>) => {
		setBusy(id);
		setError('');
		try {
			await fn();
		} catch (e) {
			setError(capabilityError(e, i18n._(msg`The connection could not be changed.`)));
		} finally {
			setBusy(null);
		}
	};
	const staff = AssistantCapabilities.canConfigure;
	const features = (c: AssistantConnectorView) => {
		if (c.definition.provider === 'github')
			return [i18n._(msg`Repositories`), i18n._(msg`Issues and pull requests`), i18n._(msg`Checks and workflows`)];
		const labels = c.definition.capabilities.map((a) => a.label);
		return labels.length > 3 ? [...labels.slice(0, 2), i18n._(msg`+${labels.length - 2} more`)] : labels;
	};
	return (
		<>
			{AssistantCapabilities.connectors.map((c) => {
				const d = c.definition;
				const managed = d.shared && !staff;
				return (
					<KonvoConnectorCard
						key={d.id}
						logo={d.provider === 'github' ? <GithubLogoIcon size={24} /> : <PlugIcon size={24} />}
						name={d.name}
						description={d.description || i18n._(msg`Custom connector`)}
						features={features(c)}
						connected={c.connected && c.health === 'ready'}
						statusLabel={
							c.health === 'reconnect'
								? i18n._(msg`Reconnect needed`)
								: c.health === 'disabled'
									? i18n._(msg`Turned off`)
									: managed && c.connected
										? i18n._(msg`Shared`)
										: undefined
						}
						account={c.account}
						actionLabel={c.connected ? i18n._(msg`Reconnect`) : i18n._(msg`Connect`)}
						busy={busy === d.id}
						note={
							!d.enabled
								? i18n._(msg`Turned off by staff`)
								: managed
									? c.connected
										? i18n._(msg`Staff connected this for everyone.`)
										: i18n._(msg`Staff haven't connected this yet.`)
									: undefined
						}
						onAction={() => {
							if (d.auth === 'oauth2')
								void run(d.id, async () => openExternalUrl(await AssistantCapabilities.oauth(d.id)));
							else ModalCommands.push(modal(() => <TokenModal connector={c} />));
						}}
						secondaryActionLabel={c.connected ? i18n._(msg`Disconnect`) : undefined}
						onSecondaryAction={
							c.connected ? () => void run(d.id, () => AssistantCapabilities.disconnect(d.id)) : undefined
						}
						accessory={
							staff ? (
								<IconButton
									label={i18n._(msg`${d.name} connector settings`)}
									icon={<GearIcon size={18} />}
									onClick={() => openConnectorEditor(d, c.version)}
								/>
							) : undefined
						}
					/>
				);
			})}
			{allowAdd && staff && (
				<button
					type="button"
					className={connectorStyles.addCard}
					onClick={() => openConnectorEditor(blankCustomConnector(), null)}
				>
					<span className={connectorStyles.addCardIcon}>
						<PlusIcon size={20} />
					</span>
					<p className={connectorStyles.addCardTitle}>
						<Trans>Add custom connector</Trans>
					</p>
					<p className={connectorStyles.addCardText}>
						<Trans>Connect any HTTPS API and choose exactly which actions the assistant may call.</Trans>
					</p>
				</button>
			)}
			{error && (
				<Notice tone="danger" style={{gridColumn: '1 / -1'}}>
					{error}
				</Notice>
			)}
			{AssistantCapabilities.connectors.flatMap((c) =>
				(c.affected_flows ?? []).map((flow) => (
					<Notice tone="warning" key={`${c.definition.id}:${flow.id}`} style={{gridColumn: '1 / -1'}}>
						{c.definition.name} · {flow.name}: {flow.reason}
					</Notice>
				)),
			)}
		</>
	);
});

export const AssistantConnectorSettings = observer(() => (
	<SettingsSection
		id="assistant-connectors"
		title={<Trans>Assistant connections</Trans>}
		description={
			AssistantCapabilities.canConfigure ? (
				<Trans>
					Let the assistant use GitHub and your team's own APIs. Add a custom connector for any other service; the
					assistant can only call the actions you list.
				</Trans>
			) : (
				<Trans>Let the assistant use GitHub and the connectors your team has added. Tokens stay encrypted.</Trans>
			)
		}
	>
		<div className={connectorStyles.grid}>
			<AssistantConnectorCards allowAdd />
		</div>
	</SettingsSection>
));
